import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, apiUploadSimple } from '../services/api';
import useEffectiveCity from '../hooks/useEffectiveCity';

/**
 * Configurações → Logomarcas: a logomarca de cada prefeitura, no topo de todo
 * PDF daquela cidade (declarações, diário, boletim, relatórios).
 *
 * Cada cidade aparece como o topo do PDF vai sair — logomarca à esquerda,
 * prefeitura e secretaria ao lado —, para quem envia ver o resultado antes
 * de gerar um documento.
 */

const ACEITOS = 'image/png,image/jpeg,image/webp';

const formatarData = (iso) => (iso
  ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '');

/** O topo do PDF, em miniatura: papel branco mesmo no tema escuro. */
function PreviaDoCabecalho({ item }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 px-4 py-3 shadow-sm">
      <div className="flex items-center gap-4 min-h-[56px]">
        {item.logo && (
          <img src={item.logo} alt={`Logomarca de ${item.city}`} className="h-12 w-auto max-w-[35%] object-contain flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0 text-center">
          <p className="text-[12px] font-bold text-slate-800 leading-tight">{item.linhas[0]}</p>
          <p className="text-[11px] font-bold text-slate-800 leading-tight mt-0.5">{item.linhas[1]}</p>
        </div>
      </div>
      <div className="mt-2 border-t border-slate-500" />
    </div>
  );
}

function LogosPrefeituras({ darkMode, cardClass, titleClass, mutedClass }) {
  const cidadeDoMenu = useEffectiveCity();
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [ocupado, setOcupado] = useState(null); // id da cidade em envio/remoção
  const [mensagens, setMensagens] = useState({}); // id → { ok, texto }
  const entrada = useRef(null);
  const destino = useRef(null); // cidade que vai receber o arquivo escolhido

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const lista = await apiGet('/logos-prefeitura/');
      setItens(Array.isArray(lista) ? lista : []);
    } catch (err) {
      setErro(err?.message || 'Não foi possível carregar as cidades.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // A cidade escolhida no menu vem primeiro: é a que o administrador está
  // atendendo agora.
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return itens
      .filter((i) => !q || `${i.city} ${i.state}`.toLowerCase().includes(q))
      .sort((a, b) => (String(b.id_city) === String(cidadeDoMenu?.id)) - (String(a.id_city) === String(cidadeDoMenu?.id)));
  }, [itens, busca, cidadeDoMenu?.id]);

  const comLogo = itens.filter((i) => i.logo).length;

  const atualizar = (novo) => setItens((lista) => lista.map((i) => (i.id_city === novo.id_city ? novo : i)));
  const avisar = (id, ok, texto) => setMensagens((m) => ({ ...m, [id]: { ok, texto } }));

  const escolherArquivo = (item) => {
    destino.current = item;
    if (entrada.current) {
      entrada.current.value = '';
      entrada.current.click();
    }
  };

  const enviar = async (e) => {
    const arquivo = e.target.files?.[0];
    const item = destino.current;
    if (!arquivo || !item) return;
    setOcupado(item.id_city);
    setMensagens((m) => ({ ...m, [item.id_city]: null }));
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      const novo = await apiUploadSimple(`/logos-prefeitura/${item.id_city}/enviar/`, fd);
      atualizar(novo);
      avisar(item.id_city, true, 'Logomarca salva. Os próximos PDFs desta cidade já saem com ela.');
    } catch (err) {
      avisar(item.id_city, false, err?.message || 'Não foi possível enviar a imagem.');
    } finally {
      setOcupado(null);
    }
  };

  const remover = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remover a logomarca de ${item.city} – ${item.state}?\n\nOs PDFs desta cidade passam a sair só com o texto da prefeitura e da secretaria.`)) return;
    setOcupado(item.id_city);
    try {
      const novo = await apiDelete(`/logos-prefeitura/${item.id_city}/`);
      if (novo) atualizar(novo);
      avisar(item.id_city, true, 'Logomarca removida.');
    } catch (err) {
      avisar(item.id_city, false, err?.message || 'Não foi possível remover.');
    } finally {
      setOcupado(null);
    }
  };

  const campo = `w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode
      ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
  }`;
  const botaoPequeno = `px-3 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap disabled:opacity-60 ${
    darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
  }`;
  const forte = darkMode ? 'text-slate-200' : 'text-slate-700';

  return (
    <div className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h2 className={`${titleClass} !mb-1`}>Logomarcas das Prefeituras</h2>
          <p className={`text-sm ${mutedClass} max-w-2xl`}>
            A logomarca sai no topo de todo PDF da cidade — declarações, diário, boletim e relatórios —,
            ao lado de “Prefeitura Municipal” e “Secretaria Municipal de Educação”. Use PNG com fundo
            transparente, de preferência horizontal, com até 2 MB.
          </p>
        </div>
        <button type="button" onClick={carregar} className={botaoPequeno}>Atualizar</button>
      </div>

      <input ref={entrada} type="file" accept={ACEITOS} className="hidden" onChange={enviar} />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 my-4 items-center">
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar cidade..."
          className={campo}
        />
        {!carregando && (
          <p className={`text-xs ${mutedClass}`}>
            {comLogo} de {itens.length} {itens.length === 1 ? 'cidade' : 'cidades'} com logomarca
          </p>
        )}
      </div>

      {erro && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C]' : 'bg-[#E83A3C]/5 text-[#E83A3C]'}`}>
          {erro}
        </div>
      )}

      {carregando ? (
        <p className={`py-8 text-center ${mutedClass}`}>Carregando cidades...</p>
      ) : filtrados.length === 0 ? (
        <p className={`py-8 text-center ${mutedClass}`}>Nenhuma cidade encontrada.</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {filtrados.map((item) => {
            const msg = mensagens[item.id_city];
            const atual = String(item.id_city) === String(cidadeDoMenu?.id);
            return (
              <div
                key={item.id_city}
                className={`p-4 rounded-lg border ${
                  atual
                    ? 'border-[#0974BA]/60'
                    : darkMode ? 'border-slate-700' : 'border-slate-200'
                } ${darkMode ? 'bg-slate-900/30' : 'bg-slate-50/60'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${forte}`}>
                      {item.city} – {item.state}
                      {atual && <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-[#0974BA]/15 text-[#0974BA]">Cidade do menu</span>}
                    </p>
                    <p className={`text-xs ${mutedClass}`}>
                      {item.logo
                        ? `Enviada${item.enviado_por ? ` por ${item.enviado_por}` : ''}${item.updated_at ? ` em ${formatarData(item.updated_at)}` : ''}`
                        : 'Sem logomarca — o PDF sai só com o texto'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => escolherArquivo(item)}
                      disabled={ocupado === item.id_city}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA] hover:bg-[#0974BA]/80 text-white disabled:opacity-60 whitespace-nowrap"
                    >
                      {ocupado === item.id_city ? 'Salvando...' : item.logo ? 'Trocar' : 'Enviar logomarca'}
                    </button>
                    {item.logo && (
                      <button type="button" onClick={() => remover(item)} disabled={ocupado === item.id_city} className={botaoPequeno}>
                        Remover
                      </button>
                    )}
                  </div>
                </div>
                <PreviaDoCabecalho item={item} />
                {msg && <p className={`mt-2 text-xs ${msg.ok ? 'text-[#13983D]' : 'text-[#E83A3C]'}`}>{msg.texto}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LogosPrefeituras;
